import { HttpInterceptorFn, HttpErrorResponse, HttpResponse, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { DecryptService } from '../services/decrypt.service';
import { PaymentDecryptService } from '../services/payment-decrypt.service';
import { SKIP_LIVEPASS_DECRYPT } from './http-context.tokens';
import { catchError, switchMap, throwError, map } from 'rxjs';

export const decryptInterceptor: HttpInterceptorFn = (req, next) => {
  const decryptService = inject(DecryptService);
  const paymentDecryptService = inject(PaymentDecryptService);

  return next(req).pipe(
    switchMap((event: HttpEvent<any>) => {
      // Only process HttpResponse events
      if (event instanceof HttpResponse) {
        if (req.context.get(SKIP_LIVEPASS_DECRYPT)) {
          return [event];
        }
        const body = event.body;
        
        // Check if response body is encrypted
        if (decryptService.isEncryptedResponse(body)) {
          const encryptedData = DecryptService.extractEncryptedString(body);
          if (encryptedData) {
            // Decrypt the response
            return decryptService.decrypt(encryptedData).pipe(
              map((decryptedData: any) => {
                // Create new HttpResponse with decrypted data
                return event.clone({
                  body: decryptedData
                }) as HttpEvent<any>;
              }),
              catchError((error) => {
                console.error('Decryption error:', error);
                // If decryption fails, return original response
                return [event];
              })
            );
          }
        }
      }
      
      // If not HttpResponse or not encrypted, return as is
      return [event];
    }),
    catchError((error: HttpErrorResponse) => {
      if (req.context.get(SKIP_LIVEPASS_DECRYPT)) {
        if (error.error != null && paymentDecryptService.isEncryptedResponse(error.error)) {
          try {
            const decrypted = paymentDecryptService.decryptErrorPayload(error.error);
            return throwError(
              () =>
                new HttpErrorResponse({
                  error: decrypted,
                  status: error.status,
                  statusText: error.statusText,
                  url: error.url || undefined,
                  headers: error.headers
                })
            );
          } catch (e) {
            console.error('Payment API decryption error for error response:', e);
          }
        }
        return throwError(() => error);
      }
      // Handle error responses that might be encrypted
      if (error.error && decryptService.isEncryptedResponse(error.error)) {
        const encryptedData = DecryptService.extractEncryptedString(error.error);
        if (encryptedData) {
          return decryptService.decrypt(encryptedData).pipe(
            switchMap((decryptedData: any) => {
              // Create new error with decrypted data
              const decryptedError = new HttpErrorResponse({
                error: decryptedData,
                status: error.status,
                statusText: error.statusText,
                url: error.url || undefined,
                headers: error.headers
              });
              return throwError(() => decryptedError);
            }),
            catchError((decryptError) => {
              console.error('Decryption error for error response:', decryptError);
              return throwError(() => error);
            })
          );
        }
      }
      
      return throwError(() => error);
    })
  );
};
